import { useState, type CSSProperties, type ReactNode } from 'react';
import { Splitter } from './Splitter';

const DEFAULT_WIDTH = 288;

function load(key: string): { open: boolean; width: number } {
  const small = typeof window !== 'undefined' && window.innerWidth < 1100;
  try {
    const raw = JSON.parse(localStorage.getItem(key) ?? 'null') as { open?: boolean; width?: number } | null;
    return { open: raw?.open ?? !small, width: raw?.width ?? (small ? 220 : DEFAULT_WIDTH) };
  } catch {
    return { open: !small, width: small ? 220 : DEFAULT_WIDTH };
  }
}

/**
 * Lista con un panel de vista previa a la derecha (Multimedia, YouTube, Fuentes): el borde entre
 * los dos se jala para hacer el reproductor más grande o más chico, y el panel se puede ocultar.
 * Ancho y si está abierto se recuerdan en esta computadora. Solo es para la consola: no manda nada
 * a las pantallas ni a OBS.
 */
export function WithPreview({ storageKey, preview, children }: { storageKey: string; preview: ReactNode; children: ReactNode }) {
  const [prefs, setPrefs] = useState(() => load(storageKey));
  const save = (next: { open: boolean; width: number }) => {
    setPrefs(next);
    try {
      localStorage.setItem(storageKey, JSON.stringify(next));
    } catch {
      // Sin almacenamiento: solo no se recuerda.
    }
  };
  const style = { '--prev-w': `${prefs.width}px` } as CSSProperties;

  if (!prefs.open) {
    return (
      <div className="withprev withprev--closed">
        <div className="withprev__list">{children}</div>
        <button type="button" className="withprev__show" title="Mostrar la vista previa" aria-label="Mostrar la vista previa" onClick={() => save({ ...prefs, open: true })}>
          ‹ Vista previa
        </button>
      </div>
    );
  }
  return (
    <div className="withprev" style={style}>
      <div className="withprev__list">{children}</div>
      <Splitter
        orientation="vertical"
        label="Ancho de la vista previa"
        // El panel está a la derecha: jalar hacia la izquierda lo hace más grande.
        measure={(el) => -((el.nextElementSibling as HTMLElement | null)?.getBoundingClientRect().width ?? prefs.width)}
        onChange={(size, el) => {
          const room = el.parentElement?.getBoundingClientRect().width ?? 1000;
          save({ ...prefs, width: Math.round(Math.max(160, Math.min(room * 0.65, -size))) });
        }}
        onReset={() => save({ ...prefs, width: DEFAULT_WIDTH })}
      />
      <aside className="withprev__pane" aria-label="Vista previa">
        <button type="button" className="withprev__hide" title="Ocultar la vista previa" aria-label="Ocultar la vista previa" onClick={() => save({ ...prefs, open: false })}>
          ›
        </button>
        {preview}
      </aside>
    </div>
  );
}
